import React, { useEffect, useState } from "react";
import { Users, Store, Package, ShoppingBag, Star, Flag, ShieldCheck, Clock, Check, X, Trash2, Plus, Settings, Ban, TrendingUp, RotateCcw, Scale, FileText, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError, SAR } from "@/lib/api";
import { Loader, Empty } from "@/components/shared";
import { StatusBadge, PaymentBadge } from "@/lib/status";
import { ReturnCard } from "@/components/returns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const TABS = [
  ["overview", "نظرة عامة", TrendingUp],
  ["yards", "طلبات التشاليح", Store],
  ["customers", "العملاء", Users],
  ["parts", "القطع", Package],
  ["orders", "الطلبات", ShoppingBag],
  ["returns", "الاسترجاعات والنزاعات", Scale],
  ["complaints", "الشكاوى", Flag],
  ["reviews", "التقييمات", Star],
  ["reports", "البلاغات", Flag],
  ["refdata", "المدن والأقسام", Plus],
  ["legal", "البيانات القانونية والسياسات", FileText],
  ["settings", "الإعدادات", Settings],
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
      <h1 className="font-head text-2xl sm:text-3xl font-black text-white mb-6 flex items-center gap-2"><ShieldCheck size={28} className="text-[var(--amber)]" /> لوحة الإدارة</h1>
      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar pb-1">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
            className={`shrink-0 flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-bold ${tab === k ? "bg-[var(--amber)] text-[#0B0F17]" : "bg-[var(--bg-card)] text-[var(--text-muted)]"}`}>
            <Icon size={15} /> {l}
          </button>
        ))}
      </div>
      {tab === "overview" && <Overview />}
      {tab === "yards" && <Yards />}
      {tab === "customers" && <Customers />}
      {tab === "parts" && <Parts />}
      {tab === "orders" && <Orders />}
      {tab === "returns" && <AdminReturns />}
      {tab === "complaints" && <AdminComplaints />}
      {tab === "reviews" && <Reviews />}
      {tab === "reports" && <Reports />}
      {tab === "refdata" && <RefData />}
      {tab === "legal" && <LegalSettings />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

function AdminReturns() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/returns").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  if (items === null) return <Loader />;
  if (items.length === 0) return <Empty text="لا توجد طلبات استرجاع أو نزاعات." />;
  return <div className="space-y-4">{items.map((r) => <ReturnCard key={r.id} req={r} role="admin" onUpdated={load} />)}</div>;
}

function AdminComplaints() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/complaints").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const resolve = async (id) => { await api.put(`/admin/complaints/${id}/resolve`); toast.success("تم حل الشكوى"); load(); };
  if (items === null) return <Loader />;
  if (items.length === 0) return <Empty text="لا توجد شكاوى." />;
  return (
    <div className="space-y-3">
      {items.map((c) => (
        <div key={c.id} className="ts-card p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--text-muted)]">{c.complaint_number} · طلب #{c.order_number}</span>
            {c.status === "open" ? <button data-testid={`resolve-complaint-${c.id}`} onClick={() => resolve(c.id)} className="rounded-lg bg-[var(--emerald)]/15 px-3 py-1.5 text-xs font-bold text-[var(--emerald)]">حل الشكوى</button> : <span className="text-xs text-[var(--emerald)]">تم الحل</span>}
          </div>
          <p className="font-bold text-white text-sm mt-1">{c.reason}</p>
          <p className="text-xs text-[var(--text-muted)]">{c.part_name} · {c.yard_name} · بواسطة {c.reporter_name}</p>
          <p className="text-sm text-[var(--text-muted)] mt-1">{c.description}</p>
          {(c.images?.length > 0 || c.videos?.length > 0) && (
            <div className="flex flex-wrap gap-2 mt-2">
              {c.images?.map((im, i) => <a key={i} href={fileUrl(im)} target="_blank" rel="noreferrer"><img src={fileUrl(im)} alt="" className="h-14 w-14 rounded-lg object-cover" /></a>)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function LegalSettings() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/settings/public").then(({ data }) => setF(data)); }, []);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      await api.put("/admin/legal-settings", {
        platform_name: f.platform_name, legal_name: f.legal_name, address: f.address,
        cr_number: f.cr_number, tax_number: f.tax_number, support_whatsapp: f.support_whatsapp,
        support_email: f.support_email, license_info: f.license_info,
      });
      toast.success("تم حفظ البيانات القانونية — ستظهر تلقائيًا في الموقع");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!f) return <Loader />;
  const Field = ({ label, k, ph }) => (
    <div>
      <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">{label}</label>
      <input data-testid={`legal-${k}`} value={f[k] || ""} onChange={up(k)} placeholder={ph}
        className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-white outline-none" />
    </div>
  );
  return (
    <div className="max-w-2xl">
      <div className="ts-card p-4 mb-4 flex gap-2 items-start bg-[var(--amber)]/5">
        <FileText size={18} className="text-[var(--amber)] shrink-0 mt-0.5" />
        <p className="text-xs text-[var(--text-muted)]">تظهر هذه البيانات تلقائيًا في الفوتر وصفحات السياسات وصفحتي «من نحن» و«تواصل معنا». نصوص السياسات نفسها متوافقة مع الأنظمة السعودية وتُحدَّث برمجيًا.</p>
      </div>
      <div className="ts-card p-6 grid sm:grid-cols-2 gap-4">
        <Field label="اسم المنصة" k="platform_name" />
        <Field label="الاسم القانوني للمنشأة" k="legal_name" />
        <div className="sm:col-span-2"><Field label="العنوان" k="address" /></div>
        <Field label="رقم السجل التجاري" k="cr_number" />
        <Field label="الرقم الضريبي (اختياري)" k="tax_number" ph="عند توفره" />
        <Field label="رقم واتساب الدعم" k="support_whatsapp" />
        <Field label="البريد الإلكتروني" k="support_email" />
        <div className="sm:col-span-2"><Field label="بيانات التراخيص (اختياري)" k="license_info" ph="عند توفرها" /></div>
        <div className="sm:col-span-2"><button data-testid="save-legal-settings" onClick={save} className="btn-amber w-full justify-center">حفظ البيانات القانونية</button></div>
      </div>
    </div>
  );
}

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)); }, []);
  if (!s) return <Loader />;
  const cards = [
    { icon: Users, label: "العملاء", value: s.customers, color: "#3B82F6" },
    { icon: Store, label: "التشاليح", value: s.yards, color: "#10B981" },
    { icon: Clock, label: "بانتظار المراجعة", value: s.yards_pending, color: "#F59E0B" },
    { icon: Package, label: "القطع", value: s.parts, color: "#3B82F6" },
    { icon: ShoppingBag, label: "الطلبات", value: s.orders, color: "#10B981" },
    { icon: TrendingUp, label: "طلبات اليوم", value: s.orders_today, color: "#F59E0B" },
    { icon: Star, label: "التقييمات", value: s.reviews, color: "#F59E0B" },
    { icon: Flag, label: "بلاغات مفتوحة", value: s.reports, color: "#EF4444" },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {cards.map((c, i) => (
          <div key={i} className="ts-card p-5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl mb-3" style={{ backgroundColor: `${c.color}22`, color: c.color }}><c.icon size={20} /></span>
            <p className="font-head text-2xl font-black text-white">{c.value}</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="ts-card p-6">
        <h3 className="font-bold text-white mb-4">أكثر القطع مشاهدة</h3>
        {s.top_parts.length === 0 ? <p className="text-sm text-[var(--text-muted)]">لا توجد بيانات بعد.</p> : (
          <div className="space-y-2">
            {s.top_parts.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-white">{p.name} <span className="text-[var(--text-muted)]">· {p.yard_name}</span></span>
                <span className="text-[var(--amber)]">{p.views || 0} مشاهدة</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Yards() {
  const [yards, setYards] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.get("/admin/yards", { params: { status: filter || undefined } }).then(({ data }) => setYards(data));
  useEffect(() => { load(); }, [filter]);

  const approve = async (id) => { await api.put(`/admin/yards/${id}/approve`); toast.success("تمت الموافقة — تم تفعيل حساب التشليح"); load(); };
  const submitReject = async () => {
    if (!reason.trim()) { toast.error("يرجى كتابة سبب الرفض"); return; }
    try {
      await api.put(`/admin/yards/${rejectTarget.id}/reject`, { status: "rejected", reason: reason.trim() });
      toast.success("تم رفض الطلب وإيقاف الحساب");
      setRejectTarget(null); setReason(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const Row = ({ Icon, label, value }) => (
    <span className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
      <Icon size={13} className="text-[var(--text-subtle)] shrink-0" />
      <span className="text-[var(--text-subtle)]">{label}:</span>
      <span className="text-[var(--text-muted)] truncate">{value || "—"}</span>
    </span>
  );

  if (yards === null) return <Loader />;
  return (
    <div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {[["pending", "بانتظار المراجعة"], ["approved", "موثقة"], ["rejected", "مرفوضة"], ["", "الكل"]].map(([v, l]) => (
          <button key={v} data-testid={`yard-filter-${v || "all"}`} onClick={() => setFilter(v)} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${filter === v ? "bg-[var(--amber)] text-[#0B0F17]" : "bg-[var(--bg-card)] text-[var(--text-muted)]"}`}>{l}</button>
        ))}
      </div>
      {yards.length === 0 ? <Empty text="لا توجد طلبات تشاليح في هذه الحالة." /> : (
        <div className="space-y-3">
          {yards.map((y) => (
            <div key={y.id} data-testid={`admin-yard-${y.id}`} className="ts-card p-4">
              <div className="flex items-start gap-3">
                <div className="h-12 w-12 rounded-xl overflow-hidden bg-[var(--bg-secondary)] shrink-0">
                  {y.logo ? <img src={fileUrl(y.logo)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center font-head font-black text-[var(--amber)]">{y.name?.[0]}</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white flex items-center gap-1.5" data-testid={`yard-name-${y.id}`}>{y.name} {y.verified && <ShieldCheck size={14} className="text-[var(--emerald)]" />}</p>
                  <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 mt-2">
                    <Row Icon={Users} label="المسؤول" value={y.owner_name} />
                    <Row Icon={Phone} label="الجوال" value={y.mobile} />
                    <Row Icon={Mail} label="البريد" value={y.email} />
                    <Row Icon={MapPin} label="المدينة" value={y.city} />
                    <Row Icon={FileText} label="السجل التجاري" value={y.cr_number} />
                    <Row Icon={Clock} label="أوقات العمل" value={y.working_hours} />
                    <span className="sm:col-span-2"><Row Icon={Store} label="العنوان" value={y.address} /></span>
                  </div>
                  {y.status === "rejected" && y.rejection_reason && <p className="text-xs text-[#EF4444] mt-2" data-testid={`yard-reject-reason-${y.id}`}>سبب الرفض: {y.rejection_reason}</p>}
                </div>
                <div className="text-left shrink-0">
                  <span data-testid={`yard-status-${y.id}`} className={`text-[10px] px-2 py-1 rounded-lg ${y.status === "approved" ? "bg-[var(--emerald)]/15 text-[var(--emerald)]" : y.status === "pending" ? "bg-[var(--amber)]/15 text-[var(--amber)]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>
                    {y.status === "approved" ? "نشط" : y.status === "pending" ? "بانتظار المراجعة" : "مرفوض/موقوف"}
                  </span>
                </div>
              </div>
              {y.status !== "approved" && (
                <div className="flex gap-2 mt-3 pt-3 border-t" style={{ borderColor: "var(--border-c)" }}>
                  <button data-testid={`approve-yard-${y.id}`} onClick={() => approve(y.id)} className="flex items-center gap-1 rounded-lg bg-[var(--emerald)]/15 px-3 py-1.5 text-xs font-bold text-[var(--emerald)]"><Check size={14} /> موافقة</button>
                  {y.status !== "rejected" && <button data-testid={`reject-yard-${y.id}`} onClick={() => { setRejectTarget(y); setReason(""); }} className="flex items-center gap-1 rounded-lg bg-[#EF4444]/15 px-3 py-1.5 text-xs font-bold text-[#EF4444]"><X size={14} /> رفض</button>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {rejectTarget && (
        <Dialog open onOpenChange={() => setRejectTarget(null)}>
          <DialogContent className="bg-[var(--bg-card)] border-[var(--border-c)] text-white max-w-md" dir="rtl">
            <DialogHeader><DialogTitle className="text-white text-right">رفض طلب التشليح</DialogTitle>
              <DialogDescription className="text-right text-[var(--text-muted)]">اكتب سبب الرفض ليظهر للتشليح.</DialogDescription></DialogHeader>
            <div className="space-y-3 mt-2">
              <p className="text-sm text-[var(--text-muted)]">سيتم إيقاف حساب «{rejectTarget.name}». يرجى كتابة سبب الرفض ليظهر للتشليح.</p>
              <textarea data-testid="reject-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                placeholder="مثال: رقم السجل التجاري غير صحيح، يرجى إعادة الإرسال بعد التصحيح"
                className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-white outline-none resize-none text-sm" />
              <div className="flex gap-2">
                <button data-testid="confirm-reject-yard" onClick={submitReject} className="flex-1 rounded-xl bg-[#EF4444] px-4 py-2.5 text-sm font-bold text-white">تأكيد الرفض</button>
                <button data-testid="cancel-reject-yard" onClick={() => setRejectTarget(null)} className="rounded-xl bg-[var(--bg-secondary)] px-4 py-2.5 text-sm font-bold text-[var(--text-muted)]">إلغاء</button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function Customers() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/customers").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const toggle = async (id) => { await api.put(`/admin/users/${id}/toggle`); load(); };
  if (items === null) return <Loader />;
  if (items.length === 0) return <Empty text="لا يوجد عملاء." />;
  return (
    <div className="space-y-2">
      {items.map((u) => (
        <div key={u.id} className="ts-card p-4 flex items-center justify-between">
          <div><p className="font-bold text-white text-sm">{u.name}</p><p className="text-xs text-[var(--text-muted)]">{u.email} · {u.mobile}</p></div>
          <button data-testid={`toggle-user-${u.id}`} onClick={() => toggle(u.id)} className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold ${u.active === false ? "bg-[var(--emerald)]/15 text-[var(--emerald)]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>
            <Ban size={13} /> {u.active === false ? "تفعيل" : "إيقاف"}
          </button>
        </div>
      ))}
    </div>
  );
}

function Parts() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/parts").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("حذف القطعة؟")) return; await api.delete(`/admin/parts/${id}`); toast.success("تم الحذف"); load(); };
  if (items === null) return <Loader />;
  if (items.length === 0) return <Empty text="لا توجد قطع." />;
  return (
    <div className="space-y-2">
      {items.map((p) => (
        <div key={p.id} className="ts-card p-3 flex items-center gap-3">
          <img src={p.images?.[0] ? fileUrl(p.images[0]) : "https://via.placeholder.com/60"} alt="" className="h-12 w-12 rounded-lg object-cover" />
          <div className="flex-1 min-w-0"><p className="font-bold text-white text-sm truncate">{p.name}</p><p className="text-xs text-[var(--text-muted)]">{p.yard_name} · {p.price.toLocaleString("ar")} {SAR}</p></div>
          <button data-testid={`admin-delete-part-${p.id}`} onClick={() => del(p.id)} className="p-2 text-[var(--text-muted)] hover:text-[#EF4444]"><Trash2 size={16} /></button>
        </div>
      ))}
    </div>
  );
}

function Orders() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/orders").then(({ data }) => setItems(data)); }, []);
  if (items === null) return <Loader />;
  if (items.length === 0) return <Empty text="لا توجد طلبات." />;
  return (
    <div className="space-y-2">
      {items.map((o) => (
        <div key={o.id} className="ts-card p-4 flex items-center justify-between gap-3">
          <div className="min-w-0"><p className="font-bold text-white text-sm">#{o.order_number}</p><p className="text-xs text-[var(--text-muted)] truncate">{o.part_snapshot?.name} · {o.customer_name} → {o.yard_name}</p><div className="mt-1"><PaymentBadge order={o} /></div></div>
          <div className="flex items-center gap-3 shrink-0"><span className="text-[var(--amber)] font-bold text-sm">{o.total.toLocaleString("ar")} {SAR}</span><StatusBadge status={o.status} /></div>
        </div>
      ))}
    </div>
  );
}

function Reviews() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/reviews").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const toggle = async (id) => { await api.put(`/admin/reviews/${id}/toggle`); load(); };
  if (items === null) return <Loader />;
  if (items.length === 0) return <Empty text="لا توجد تقييمات." />;
  return (
    <div className="space-y-2">
      {items.map((r) => (
        <div key={r.id} className="ts-card p-4">
          <div className="flex items-center justify-between">
            <span className="font-bold text-white text-sm">{r.customer_name} — {r.stars}★</span>
            <button data-testid={`toggle-review-${r.id}`} onClick={() => toggle(r.id)} className={`text-xs font-bold ${r.hidden ? "text-[var(--emerald)]" : "text-[#EF4444]"}`}>{r.hidden ? "إظهار" : "إخفاء"}</button>
          </div>
          {r.comment && <p className="text-xs text-[var(--text-muted)] mt-1">{r.comment}</p>}
        </div>
      ))}
    </div>
  );
}

function Reports() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/reports").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const resolve = async (id) => { await api.put(`/admin/reports/${id}/resolve`); toast.success("تم حل البلاغ"); load(); };
  if (items === null) return <Loader />;
  if (items.length === 0) return <Empty text="لا توجد بلاغات." />;
  return (
    <div className="space-y-2">
      {items.map((r) => (
        <div key={r.id} className="ts-card p-4 flex items-center justify-between">
          <div><p className="font-bold text-white text-sm">{r.reason}</p><p className="text-xs text-[var(--text-muted)]">{r.target_type} · بواسطة {r.reporter_name}</p></div>
          {r.status === "open" ? <button data-testid={`resolve-report-${r.id}`} onClick={() => resolve(r.id)} className="rounded-lg bg-[var(--emerald)]/15 px-3 py-1.5 text-xs font-bold text-[var(--emerald)]">حل البلاغ</button> : <span className="text-xs text-[var(--emerald)]">تم الحل</span>}
        </div>
      ))}
    </div>
  );
}

function RefData() {
  const [cities, setCities] = useState([]);
  const [cats, setCats] = useState([]);
  const [makes, setMakes] = useState([]);
  const load = () => {
    api.get("/cities").then(({ data }) => setCities(data));
    api.get("/categories").then(({ data }) => setCats(data));
    api.get("/vehicles/makes").then(({ data }) => setMakes(data));
  };
  useEffect(() => { load(); }, []);

  const add = async (endpoint, name, testid) => {
    if (!name) return;
    try { await api.post(endpoint, { name }); toast.success("تمت الإضافة"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const Block = ({ title, list, endpoint, testid }) => {
    const [val, setVal] = useState("");
    return (
      <div className="ts-card p-5">
        <h3 className="font-bold text-white mb-3">{title}</h3>
        <div className="flex gap-2 mb-3">
          <input data-testid={`${testid}-input`} value={val} onChange={(e) => setVal(e.target.value)} placeholder="إضافة جديد" className="flex-1 rounded-xl bg-[var(--bg-secondary)] px-3 py-2 text-white outline-none text-sm" />
          <button data-testid={`${testid}-add`} onClick={() => { add(endpoint, val); setVal(""); }} className="btn-amber !px-3 !py-2"><Plus size={16} /></button>
        </div>
        <div className="flex flex-wrap gap-2">
          {list.map((x) => <span key={x.id} className="rounded-lg bg-[var(--bg-secondary)] px-3 py-1.5 text-xs text-[var(--text-muted)]">{x.name}</span>)}
        </div>
      </div>
    );
  };

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <Block title="المدن" list={cities} endpoint="/admin/cities" testid="admin-city" />
      <Block title="الأقسام" list={cats} endpoint="/admin/categories" testid="admin-category" />
      <Block title="الشركات المصنعة" list={makes} endpoint="/admin/makes" testid="admin-make" />
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS({ subscriptions_enabled: false, online_payment_enabled: false, commission_percent: 0, subscription_price: 149, ...data })); }, []);
  const save = async () => { await api.put("/admin/settings", { subscriptions_enabled: s.subscriptions_enabled, online_payment_enabled: s.online_payment_enabled, commission_percent: 0, subscription_price: +s.subscription_price }); toast.success("تم حفظ الإعدادات"); };
  if (!s) return <Loader />;
  const Toggle = ({ label, desc, val, onChange, testid }) => (
    <div className="ts-card p-4 flex items-center justify-between">
      <div><p className="font-bold text-white text-sm">{label}</p><p className="text-xs text-[var(--text-muted)]">{desc}</p></div>
      <button data-testid={testid} onClick={() => onChange(!val)} className={`relative h-6 w-11 rounded-full transition-colors ${val ? "bg-[var(--amber)]" : "bg-[var(--border-c)]"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${val ? "right-0.5" : "left-0.5"}`} />
      </button>
    </div>
  );
  return (
    <div className="max-w-xl space-y-3">
      <div className="ts-card p-4 bg-[var(--emerald)]/5 mb-4">
        <p className="text-sm text-[var(--emerald)] font-bold">💚 المنصة مجانية للتشاليح — لا عمولة على المبيعات</p>
        <p className="text-xs text-[var(--text-muted)] mt-1">قيمة بيع القطعة كاملة تعود للتشليح. طريقة الدفع الحالية: الدفع عند الاستلام.</p>
      </div>
      <Toggle label="تفعيل نظام الاشتراكات" desc="باقات التشاليح المدفوعة (مستقبلًا)" val={s.subscriptions_enabled} onChange={(v) => setS({ ...s, subscriptions_enabled: v })} testid="setting-subscriptions" />
      <Toggle label="تفعيل الدفع الإلكتروني" desc="مدى/Visa/Apple Pay (مستقبلًا)" val={s.online_payment_enabled} onChange={(v) => setS({ ...s, online_payment_enabled: v })} testid="setting-online-payment" />
      <div className="ts-card p-4">
        <p className="font-bold text-white text-sm mb-1">قيمة الاشتراك الشهري — خطة واحدة (ريال)</p>
        <p className="text-xs text-[var(--text-muted)] mb-2">خطة اشتراك واحدة لكل التشاليح. يُطبَّق بعد انتهاء الفترة المجانية (٣ أشهر). أي سعر تحفظه يصبح المعتمد للاشتراكات الجديدة. لا عمولة على المبيعات.</p>
        <input data-testid="setting-subscription-price" type="number" min="0" value={s.subscription_price}
          onChange={(e) => setS({ ...s, subscription_price: e.target.value })}
          className="w-40 rounded-xl bg-[var(--bg-secondary)] px-4 py-2.5 text-white outline-none" />
      </div>
      <button data-testid="save-settings" onClick={save} className="btn-amber">حفظ الإعدادات</button>
    </div>
  );
}
